import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {NgbModal, NgbPaginationModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {Contract, ContractChange, isContractActive, isContractDeactivated} from '@shared/api/models/query/contract.model';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {Pagination} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {DeactivateContractModal} from '../modals/deactivate-contract.modal';

/**
 * UNIVAPLAT-3519: History tab of the contract detail page. The detail page only renders it for
 * users holding VIEW_CONTRACT_HISTORY.
 *
 * - Paginated list of deactivate / reactivate events, newest first (reasons are shown as plain text).
 * - Deactivate (with optional reason) and Reactivate (confirm) are offered only to
 *   CHANGE_CONTRACT_STATE holders, and only while the state allows it (not for deleted contracts).
 * - After a successful change the history reloads and `changed` tells the detail page to reload
 *   the contract so the header badge and General details tab flip read-only / editable.
 * - Backend owns the error text, shown verbatim. Reasons are never logged and dropped on destroy.
 */
@Component({
    selector: 'cdss-contract-history-tab',
    standalone: true,
    imports: [DatePipe, NgbPaginationModule, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './contract-history-tab.component.html',
    styleUrl: './contract-history-tab.component.scss'
})
export class ContractHistoryTabComponent implements OnChanges, OnDestroy {
    @Input() contract!: Contract;
    /** Emits after a deactivate / reactivate landed (or the API says the state had already changed). */
    @Output() changed = new EventEmitter<void>();

    static readonly PAGE_SIZE = 10;

    events: ContractChange[] = [];
    loading = false;
    loadError: string | null = null;
    /** 1-based page for ngb-pagination. */
    page = 1;
    total = 0;
    readonly pageSize = ContractHistoryTabComponent.PAGE_SIZE;
    /** Double-submit guard for the reactivate request. */
    reactivating = false;

    @ViewChild('stateButton') private stateButton?: ElementRef<HTMLButtonElement>;

    private loadSeq = 0;

    constructor(
        private contractsService: ContractsService,
        private authService: AuthService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    get canChangeState(): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[Permissions.CHANGE_CONTRACT_STATE] ?? []).some(role => userRoles.includes(role));
    }

    get canDeactivate(): boolean {
        return !!this.contract && isContractActive(this.contract);
    }

    get canReactivate(): boolean {
        return !!this.contract && !this.contract.deletedAt && isContractDeactivated(this.contract);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['contract']) {
            return;
        }
        const previous = changes['contract'].previousValue as Contract | undefined;
        const current = this.contract;
        if (!previous || previous.id !== current?.id) {
            this.page = 1;
            void this.load();
        } else if (!!previous.disabledAt !== !!current.disabledAt || !!previous.deletedAt !== !!current.deletedAt) {
            // State flipped elsewhere (e.g. a refused save): show the new event.
            this.page = 1;
            void this.load();
        }
    }

    ngOnDestroy(): void {
        this.loadSeq++;
        this.events = [];
    }

    async load(): Promise<void> {
        const seq = ++this.loadSeq;
        if (!this.contract) {
            return;
        }
        this.loading = true;
        this.loadError = null;
        try {
            const result = await this.contractsService.listLogs(
                this.contract.id,
                new Pagination(this.pageSize, this.page - 1));
            if (seq === this.loadSeq) {
                this.events = result.data;
                this.total = result.pagination?.totalCount ?? result.data.length;
            }
        } catch (error: any) {
            if (seq === this.loadSeq) {
                this.events = [];
                this.total = 0;
                this.loadError = error?.error?.error ?? 'The contract history could not be loaded.';
            }
        } finally {
            if (seq === this.loadSeq) {
                this.loading = false;
            }
        }
    }

    onPageChange(page: number): void {
        this.page = page;
        void this.load();
    }

    changeLabel(event: ContractChange): string {
        return event.change === 'deactivate' ? 'Deactivated' : 'Reactivated';
    }

    async openDeactivateModal(): Promise<void> {
        if (!this.canChangeState || !this.canDeactivate) {
            return;
        }
        const ref = this.modalService.open(DeactivateContractModal, {
            centered: true,
            ariaLabelledBy: 'deactivate-contract-title',
            ariaDescribedBy: 'deactivate-contract-description'
        });
        ref.componentInstance.contractId = this.contract.id;
        const done = await ref.result.catch(() => false);
        if (done) {
            await this.afterChange();
        } else {
            this.focusStateButton();
        }
    }

    async reactivate(): Promise<void> {
        if (this.reactivating || !this.canChangeState || !this.canReactivate) {
            return;
        }
        this.reactivating = true;
        try {
            await this.contractsService.reactivate(this.contract.id);
            this.toastr.success('Contract reactivated', 'Success');
            await this.afterChange();
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
            if (error?.status === 409) {
                // Already reactivated meanwhile: refresh the state.
                await this.afterChange();
            }
        } finally {
            this.reactivating = false;
        }
    }

    private async afterChange(): Promise<void> {
        this.page = 1;
        await this.load();
        this.changed.emit();
        this.focusStateButton();
    }

    /** The state button is replaced by the opposite action after a change; return focus to whichever is shown. */
    private focusStateButton(): void {
        this.cdr.detectChanges();
        this.stateButton?.nativeElement.focus();
    }
}
